import {
  COMPANY_CAM_TOKEN_ERROR_MESSAGE,
  INTEGRATION_ERROR_TITLES,
} from "./constants";
import type { IntegrationProvider } from "./types";

type ApiErrorBody = {
  error?: string;
  message?: string;
  detail?: string | string[];
  ai_error?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readResponseData(error: unknown): ApiErrorBody | null {
  if (!isRecord(error)) return null;
  const response = error.response;
  if (!isRecord(response) || !isRecord(response.data)) return null;
  return response.data as ApiErrorBody;
}

function readDetail(detail: string | string[] | undefined): string | undefined {
  if (!detail) return undefined;
  if (Array.isArray(detail)) {
    return detail[0] || undefined;
  }
  return detail;
}

/**
 * Extract a user-facing message from Swivu API (or axios) errors.
 * Prefers `error`, then `detail`, then `message`, then `ai_error`.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = readResponseData(error);
  if (data?.error) return data.error;

  const detail = readDetail(data?.detail);
  if (detail) return detail;

  if (data?.message) return data.message;

  if (data?.ai_error) return data.ai_error;

  if (isRecord(error) && typeof error.message === "string" && error.message) {
    return error.message;
  }

  return fallback;
}

/**
 * Bryntum rejects failed project.sync() as CrudManagerRequestError with
 * message "Sync failed". When responseText is present, requestFail already
 * shows the real API error (or recovery dialog), so local catch toasts should
 * skip it. When responseText is empty (offline/timeout), requestFail returns
 * early with no UI — keep the local toast.
 */
export function isBoardSyncRequestError(error: unknown): boolean {
  if (!isRecord(error)) return false;
  const isSync =
    error.action === "sync" ||
    (isRecord(error.request) && error.request.type === "sync") ||
    error.message === "Sync failed";
  if (!isSync) return false;
  return typeof error.responseText === "string" && error.responseText.length > 0;
}

export function isOpenAiKeyRelatedError(error: unknown): boolean {
  const data = readResponseData(error);
  if (data?.ai_error) return true;

  const parts = [data?.error, readDetail(data?.detail), data?.message].filter(
    (value): value is string => typeof value === "string" && value.length > 0
  );
  const combined = parts.join(" ").toLowerCase();

  if (!combined) return false;

  return (
    combined.includes("invalid api key") ||
    combined.includes("api key required") ||
    combined.includes("openai api key") ||
    combined.includes("missing api key") ||
    combined.includes("no api key")
  );
}

export function getCompanyCamErrorMessage(error: unknown, fallback = COMPANY_CAM_TOKEN_ERROR_MESSAGE): string {
  const status = isRecord(error) && isRecord(error.response) ? error.response.status : undefined;
  if (status === 401 || status === 403) {
    return COMPANY_CAM_TOKEN_ERROR_MESSAGE;
  }
  return getApiErrorMessage(error, fallback);
}

export function getIntegrationErrorTitle(provider: IntegrationProvider): string {
  return INTEGRATION_ERROR_TITLES[provider];
}

export function providerFromExtractionSource(source: "swivu" | "companycam"): IntegrationProvider {
  return source === "companycam" ? "company_cam" : "openai";
}
